import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { PackageSearch, Search, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ProductCard } from "@/components/catalog/ProductCard";
import { MarketingCarousel } from "@/components/catalog/MarketingCarousel";
import { BUDGET_RANGES, FilterSidebar } from "@/components/catalog/FilterSidebar";
import { RecentSearches, pushRecentSearch, pushRecentlyViewed } from "@/components/catalog/RecentSearches";
import { FeatureIcon } from "@/components/FeatureIcon";
import { API, BACKEND_URL, api } from "@/lib/api";

const money = (value) => `₹${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export default function CustomerCatalog() {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [lightboxQuantity, setLightboxQuantity] = useState(1);
  const [recentTick, setRecentTick] = useState(0);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedRanges, setSelectedRanges] = useState([]);
  const [features, setFeatures] = useState([]);
  const [seoCategories, setSeoCategories] = useState([]);
  const [invalidSlug, setInvalidSlug] = useState(false);
  const { slug: routeSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const categories = seoCategories.map((category) => category.name);
  const visibleProducts = products.filter((product) => {
    const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(product.category);
    const price = Number(product.retail_price || 0);
    const budgetMatch =
      selectedRanges.length === 0 ||
      selectedRanges.some((rangeId) => {
        const range = BUDGET_RANGES.find((item) => item.id === rangeId);
        return range ? price >= range.min && price < range.max : false;
      });
    return categoryMatch && budgetMatch;
  });

  const toggleValue = (value, setter) =>
    setter((current) => (current.includes(value) ? current.filter((item) => item !== value) : [...current, value]));

  useEffect(() => {
    if (!routeSlug || seoCategories.length === 0) return;
    const match = seoCategories.find((category) => category.slug === routeSlug);
    if (match) {
      setInvalidSlug(false);
      setSelectedCategories([match.name]);
    } else {
      setInvalidSlug(true);
    }
  }, [routeSlug, seoCategories]);

  useEffect(() => {
    const match = routeSlug ? seoCategories.find((category) => category.slug === routeSlug) : null;
    document.title = match ? match.meta_title || `${match.name} | BusinessBox.co.in` : "BusinessBox.co.in | Intelligent Import Engine";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", match ? match.meta_description || match.description || "BusinessBox.co.in wholesale catalog." : "Corporate gifting & business gifting solutions in India — premium corporate gift boxes, executive gifting, client appreciation gifts, employee recognition gifts, onboarding welcome kits, work anniversary gifts and VIP client gifts at transparent bulk prices.");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", match ? `${BACKEND_URL}/catalog/${match.slug}` : `${BACKEND_URL}/`);
  }, [routeSlug, seoCategories]);

  const handleToggleCategory = (category) => {
    const next = selectedCategories.includes(category)
      ? selectedCategories.filter((item) => item !== category)
      : [...selectedCategories, category];
    setSelectedCategories(next);
    const match = next.length === 1 ? seoCategories.find((item) => item.name === next[0]) : null;
    const target = match ? `/catalog/${match.slug}` : "/";
    if (location.pathname !== target) navigate(target);
  };

  const handleSelectProduct = (selected) => {
    setSelectedProduct(selected);
    setLightboxQuantity(selected.minimum_quantity || 1);
    pushRecentlyViewed(selected.id);
    setRecentTick((tick) => tick + 1);
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const [productResponse, bannerResponse, featureResponse, categoryResponse] = await Promise.all([
          api.get("/catalog/products", { params: search ? { search } : {} }),
          api.get("/catalog/banners"),
          api.get("/features"),
          api.get("/categories"),
        ]);
        setProducts(productResponse.data);
        setBanners(bannerResponse.data);
        setFeatures(featureResponse.data);
        setSeoCategories(categoryResponse.data);
        if (search.trim()) pushRecentSearch(search.trim());
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-gray-950" data-testid="customer-catalog-page">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-5 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.24em] text-blue-700">
              <ShieldCheck className="h-4 w-4" /> BusinessBox.co.in
            </div>
            <h1 className="mt-2 font-headline text-4xl font-bold tracking-tight text-gray-950 sm:text-5xl">Corporate Gifting &amp; Business Gifting Solutions — Gift Boxes, Executive Gifts &amp; Welcome Kits</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-gray-600">Live bulk prices on every product — transparent GST-inclusive rates and MOQ upfront, so you never wait 48 hours for a quote.</p>
            <p className="mt-4 max-w-3xl text-[11px] leading-5 text-gray-400" data-testid="seo-intro-paragraph">Explore premium corporate gift boxes, executive gifting, client appreciation gifts, employee recognition gifts, onboarding welcome kits, work anniversary gifts, and VIP client gifts. Trusted by businesses across India for bulk corporate gifting and client gifting needs.</p>
          </div>
          <Link to="/login" data-testid="catalog-admin-login-link" className="w-fit rounded-full border border-blue-200 bg-blue-50 px-5 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-100">
            Admin login
          </Link>
        </div>
      </header>
      <MarketingCarousel banners={banners} />
      {features.length > 0 ? (
        <section className="mx-auto max-w-7xl px-4 pt-8" data-testid="features-section">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature) => (
              <div key={feature.id} className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={`feature-card-${feature.id}`}>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
                  <FeatureIcon name={feature.icon} className="h-5 w-5" />
                </div>
                <h3 className="font-headline text-lg font-semibold text-gray-950" data-testid={`feature-title-${feature.id}`}>{feature.title}</h3>
                <p className="mt-1 text-sm leading-6 text-gray-600" data-testid={`feature-description-${feature.id}`}>{feature.description}</p>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <section className="mx-auto max-w-7xl px-4 pb-16">
        <RecentSearches products={products} onSelect={handleSelectProduct} tick={recentTick} />
        {invalidSlug ? (
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="invalid-category-notice">
            That category link does not exist. Showing all products instead.
          </div>
        ) : null}
        <div className="sticky top-0 z-10 -mx-4 border-y border-gray-200 bg-[#FAFAFA]/95 px-4 py-4 backdrop-blur-md">
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="customer-catalog-search-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search products, colors, categories..."
              className="h-12 rounded-full border-gray-300 bg-white pl-10 shadow-sm"
            />
          </div>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-500" data-testid="catalog-loading-state">Loading published catalog...</div>
        ) : products.length === 0 ? (
          <div className="flex min-h-80 flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white text-center" data-testid="catalog-empty-state">
            <PackageSearch className="mb-4 h-12 w-12 text-gray-400" />
            <h2 className="font-headline text-2xl font-semibold">No published products yet</h2>
            <p className="mt-2 max-w-md text-sm text-gray-600">Products appear here after an admin reviews extracted data and approves the batch.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-6 py-8 lg:flex-row">
            <FilterSidebar
              categories={categories}
              selectedCategories={selectedCategories}
              onToggleCategory={handleToggleCategory}
              selectedRanges={selectedRanges}
              onToggleRange={(rangeId) => toggleValue(rangeId, setSelectedRanges)}
              onClear={() => {
                setSelectedCategories([]);
                setSelectedRanges([]);
              }}
            />
            <div className="flex-1">
              <div className="mb-4 text-sm text-gray-500" data-testid="catalog-result-count">
                Showing {visibleProducts.length} of {products.length} products
              </div>
              {visibleProducts.length === 0 ? (
                <div className="flex min-h-80 flex-col items-center justify-center rounded-3xl border border-dashed border-gray-300 bg-white text-center" data-testid="catalog-filtered-empty-state">
                  <PackageSearch className="mb-4 h-12 w-12 text-gray-400" />
                  <h2 className="font-headline text-2xl font-semibold">No products match these filters</h2>
                  <p className="mt-2 max-w-md text-sm text-gray-600">Try clearing a category or budget range to see more products.</p>
                </div>
              ) : (
                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-3" data-testid="customer-product-grid">
                  {visibleProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onSelect={handleSelectProduct}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </section>
      <Dialog open={Boolean(selectedProduct)} onOpenChange={(open) => !open && setSelectedProduct(null)}>
        <DialogContent className="max-w-2xl overflow-hidden border-gray-200 bg-white p-0" data-testid="product-lightbox">
          {selectedProduct ? (
            <div className="grid md:grid-cols-2">
              <div className="flex min-h-64 items-center justify-center bg-gray-100">
                {selectedProduct.images?.[0]?.file_id ? (
                  <img
                    src={`${API}/public/products/${selectedProduct.id}/image/${selectedProduct.images[0].file_id}`}
                    alt={selectedProduct.name}
                    className="h-full w-full object-cover"
                    data-testid="lightbox-product-image"
                  />
                ) : (
                  <PackageSearch className="h-12 w-12 text-gray-400" />
                )}
              </div>
              <div className="flex flex-col gap-4 p-6">
                <DialogHeader>
                  <div className="mb-1 flex flex-wrap items-center gap-2">
                    <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">MOQ {selectedProduct.moq} pcs</Badge>
                    {selectedProduct.category ? <Badge className="border-blue-200 bg-blue-50 text-blue-700">{selectedProduct.category}</Badge> : null}
                  </div>
                  <DialogTitle className="font-headline text-2xl text-gray-950" data-testid="lightbox-product-name">{selectedProduct.name}</DialogTitle>
                  <DialogDescription className="leading-6 text-gray-600" data-testid="lightbox-product-description">
                    {selectedProduct.description}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap gap-2">
                  {(selectedProduct.colors || []).map((color) => (
                    <span key={color} className="rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs text-gray-600">{color}</span>
                  ))}
                </div>
                <div className="flex items-center gap-3" data-testid="lightbox-quantity-control">
                  <label htmlFor="lightbox-quantity-input" className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">Quantity</label>
                  <input
                    id="lightbox-quantity-input"
                    data-testid="lightbox-quantity-input"
                    type="number"
                    min={selectedProduct.minimum_quantity || 1}
                    step="1"
                    value={lightboxQuantity}
                    onChange={(event) => {
                      const parsed = Math.floor(Number(event.target.value));
                      const minimum = selectedProduct.minimum_quantity || 1;
                      setLightboxQuantity(Number.isNaN(parsed) ? minimum : Math.max(minimum, parsed));
                    }}
                    className="h-10 w-24 rounded-lg border border-gray-300 px-3 text-center text-sm"
                  />
                </div>
                {selectedProduct.minimum_quantity > 1 ? (
                  <p className="text-xs font-semibold text-amber-600" data-testid="lightbox-min-quantity-message">Minimum quantity for this product is {selectedProduct.minimum_quantity}</p>
                ) : null}
                <div className="mt-auto flex items-end justify-between border-t border-gray-100 pt-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-gray-500">Bulk price</div>
                    <div className="font-headline text-3xl font-bold text-gray-950" data-testid="lightbox-product-price">{money(selectedProduct.retail_price)}</div>
                    <div className="text-xs text-gray-500">incl. GST{selectedProduct.hsn_code ? ` · HSN ${selectedProduct.hsn_code}` : ""}</div>
                  </div>
                  <div className="rounded-xl bg-blue-600 px-3 py-2 font-mono text-xs text-white">{selectedProduct.public_sku}</div>
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
